import type { HipSpineAnnotationCategory } from '@/domain/anatomy/hipSpineAngleOverlays';
import standingImg from '@/assets/planner/standing-sagittal-xray.png';
import seatedImg from '@/assets/planner/seated-sagittal-xray.png';
import lumbarImg from '@/assets/planner/lumbar-sagittal-xray.png';
import standingLumbarOverlay from '@/assets/planner/hip-spine-help/standing-sagittal-lumbar.svg';
import standingSacrumOverlay from '@/assets/planner/hip-spine-help/standing-sagittal-sacrum.svg';
import standingFemoralOverlay from '@/assets/planner/hip-spine-help/standing-sagittal-femoral.svg';
import standingPelvisOverlay from '@/assets/planner/hip-spine-help/standing-sagittal-pelvis.svg';
import seatedLumbarOverlay from '@/assets/planner/hip-spine-help/flexed-seated-sagittal-lumbar.svg';
import seatedSacrumOverlay from '@/assets/planner/hip-spine-help/flexed-seated-sagittal-sacrum.svg';
import lumbarLumbarOverlay from '@/assets/planner/hip-spine-help/lumbar-sagittal-lumbar.svg';
import lumbarSacrumOverlay from '@/assets/planner/hip-spine-help/lumbar-sagittal-sacrum.svg';

/** Posture-independent guidance copy for an annotation. */
export type AnnotationHelpContent = {
    title: string;
    description: string;
    bullets: readonly string[];
};

export const HIP_SPINE_ANNOTATION_HELP: Record<HipSpineAnnotationCategory, AnnotationHelpContent> = {
    lumbar: {
        title: 'How to measure the Lumbar Vertebra End Plate',
        description:
            'A line along the superior endplate of the highest visible lumbar vertebrae selected in the dropdown',
        bullets: [
            'When adjusting this annotation in the x-ray, place the annotation line parallel to the superior endplate.',
            'Ensure the line spans the full width of the vertebral body.',
            'Use the highest visible lumbar vertebra for accurate measurement.',
        ],
    },
    sacrum: {
        title: 'How to measure the Sacrum End Plate',
        description: 'A line along the superior endplate of the sacrum',
        bullets: [
            'Align the line with the superior endplate of S1 (the sacral base).',
            'Extend it from the anterior to the posterior edge of the endplate.',
        ],
    },
    femoral: {
        title: 'How to measure the Proximal Femurs',
        description:
            'A line between the hip joint center and the anterior cortex of the proximal femur',
        bullets: [
            'Align the line with the femoral neck axis and cortical margins.',
            'Pass through the femoral head midpoint for consistency.',
        ],
    },
    pelvis: {
        title: 'How to measure the Anterior Pelvic Plane',
        description: 'A triangle formed by the ASIS and the pubic symphysis',
        bullets: [
            'Mark both ASIS points and the pubic symphysis.',
            'Connect the points to form the anterior pelvic plane triangle.',
            'Ensure ASIS points are level for consistent measurement.',
        ],
    },
};

const HELP_IMAGE_BY_POSTURE: Record<string, string> = {
    'standing-sagittal': standingImg,
    'flexed-seated-sagittal': seatedImg,
    'lumbar-sagittal': lumbarImg,
};

/**
 * Per-posture annotation overlays. Each is a transparent SVG (viewBox 0 0 377
 * 481, design-authored) layered over the matching x-ray. Flexed-seated and
 * lumbar postures only expose the lumbar and sacrum annotations.
 */
const HELP_OVERLAY_BY_POSTURE: Record<
    string,
    Partial<Record<HipSpineAnnotationCategory, string>>
> = {
    'standing-sagittal': {
        lumbar: standingLumbarOverlay,
        sacrum: standingSacrumOverlay,
        femoral: standingFemoralOverlay,
        pelvis: standingPelvisOverlay,
    },
    'flexed-seated-sagittal': {
        lumbar: seatedLumbarOverlay,
        sacrum: seatedSacrumOverlay,
    },
    'lumbar-sagittal': {
        lumbar: lumbarLumbarOverlay,
        sacrum: lumbarSacrumOverlay,
    },
};

/** The posture-independent help copy for an annotation category. */
export function helpContentFor(category: HipSpineAnnotationCategory): AnnotationHelpContent {
    return HIP_SPINE_ANNOTATION_HELP[category];
}

/** The help x-ray for a posture, or '' when the posture is unknown. */
export function helpImageFor(posture: string): string {
    return HELP_IMAGE_BY_POSTURE[posture] ?? '';
}

/** The overlay SVG for an annotation on a posture, or undefined when absent. */
export function overlayImageFor(
    posture: string,
    category: HipSpineAnnotationCategory,
): string | undefined {
    return HELP_OVERLAY_BY_POSTURE[posture]?.[category];
}
